/**
 * RemoveBehaviorCommand
 *
 * Records removing a behavior from a SceneObject.
 * Undo re-adds it with the saved data; redo removes it again.
 */

import type { UndoCommand, UndoOptions, SerializedCommand } from './UndoCommand';
import type { BehaviorData } from '../../scenedata/behaviordata';
import type { SceneObject } from '../../core/SceneObject';
import type { SceneObjectRef } from '../../core/SceneObjectIdentity';
import { getBehaviorConstructor } from '../../behaviors/BehaviorRegistry';
import { dispatchBehaviorChanged } from './editor-events';
import { registerCommandCodec } from './CommandCodec';

const CODEC_TYPE = 'RemoveBehavior';

interface RemoveBehaviorPayload {
    objectPath: string;
    behaviorType: string;
    behaviorData: BehaviorData;
}

export class RemoveBehaviorCommand implements UndoCommand {
    readonly label: string;
    /** Shared identity ref — survives Create/Delete undo cycles. */
    private identity: SceneObjectRef;
    private behaviorType: string;
    private behaviorData: BehaviorData;
    private pathAtConstruction: string;

    constructor(sceneObject: SceneObject, behaviorType: string, behaviorData: BehaviorData) {
        this.identity = sceneObject.traceScene.objectIdentityRegistry.refFor(sceneObject);
        this.behaviorType = behaviorType;
        this.behaviorData = structuredClone(behaviorData);
        this.pathAtConstruction = sceneObject.getPath();
        this.label = `Remove ${behaviorType} from ${sceneObject.name}`;
    }

    async undo(options?: UndoOptions): Promise<void> {
        const obj = this.identity.get();
        if (!obj) return;
        const Ctor = getBehaviorConstructor(this.behaviorType);
        if (!Ctor) return;
        await obj.addBehavior(Ctor, this.behaviorData);
        if (options?.silent) return;
        obj.traceScene?.select(obj);
        dispatchBehaviorChanged({
            objectPath: obj.getPath(),
            behaviorType: this.behaviorType,
            action: 'added',
        });
    }

    // Async for symmetry with `undo()` — `removeBehavior` is synchronous
    // today, but declaring both halves as `Promise<void>` prevents a trap
    // where a caller awaiting `cmd.redo()` in one code path silently drops
    // into a synchronous return in another.
    async redo(): Promise<void> {
        const obj = this.identity.get();
        if (!obj) return;
        const Ctor = getBehaviorConstructor(this.behaviorType);
        if (!Ctor) return;
        obj.removeBehavior(Ctor);
        obj.traceScene?.select(obj);
        dispatchBehaviorChanged({
            objectPath: obj.getPath(),
            behaviorType: this.behaviorType,
            action: 'removed',
        });
    }

    serialize(): SerializedCommand {
        const obj = this.identity.get();
        const payload: RemoveBehaviorPayload = {
            objectPath: obj ? obj.getPath() : this.pathAtConstruction,
            behaviorType: this.behaviorType,
            behaviorData: structuredClone(this.behaviorData),
        };
        return { type: CODEC_TYPE, data: payload };
    }
}

registerCommandCodec(CODEC_TYPE, (data, scene): RemoveBehaviorCommand => {
    const p = data as RemoveBehaviorPayload;
    const obj = scene.resolveSceneObject(p.objectPath);
    if (!obj) {
        throw new Error(`[RemoveBehaviorCommand] Cannot resolve path "${p.objectPath}"`);
    }
    return new RemoveBehaviorCommand(obj, p.behaviorType, p.behaviorData);
});
